import { DealLineItem, GetDealResponse, PRICING_STRATEGIES, PRODUCT_TYPES } from '@schuettflix/interfaces';
import { WarningIcon } from '@schuettflix/planum-icons-react';
import { Checkbox, Table } from '@schuettflix/react-components';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ProductTypeSelection } from '@/modules/salesforceDealImporter/components/ProductTypeSelection.tsx';
import { DealLineItemPositionReference } from './DealLineItemPositionReference';
import clsx from 'clsx';
import { matchPricingStrategyByDealPricesAndProductType } from '../utils/matchPricingStrategyByDealPricesAndProductType';
import { useFormatters } from '@/shared/hooks/useFormatters/useFormatters';
import { SUPPORTED_UNIT_LABEL } from '@/phrase/SupportedUnitLabels.ts';
import { PRODUCT_NAME_LABEL } from '@/phrase/ProductNameLabels.ts';

function PriceCell({ value, currencyCode }: { value: number | null; currencyCode: string | null }) {
    const { formatNumberWithCurrency } = useFormatters();
    return (
        <Table.Item.Text className="group-[.is-disabled]:text-disabled">
            {typeof value === 'number' ? formatNumberWithCurrency(value, currencyCode ?? undefined) : '-'}
        </Table.Item.Text>
    );
}

function PartnerOrganization({
    organization,
    type,
}: {
    organization?: { name: string } | null;
    type: 'SUPPLIER' | 'CARRIER';
}) {
    const { t } = useTranslation();
    const organizationTypeLabelKeys = {
        SUPPLIER: 'salesforceDealImporter.dealLineItem.partnerOrganizationTypes.SUPPLIER',
        CARRIER: 'salesforceDealImporter.dealLineItem.partnerOrganizationTypes.CARRIER',
    } as const;

    return (
        <span className="flex gap-1">
            {organization ? (
                <>
                    <span className="font-copy-md-strong group-[.is-disabled]:text-disabled">
                        {t(organizationTypeLabelKeys[type])}:
                    </span>
                    <span className="group-[.is-disabled]:text-disabled">{organization.name}</span>
                </>
            ) : (
                <>
                    <WarningIcon className="text-critical" monocrome />
                    <span className="text-critical font-copy-md-strong">{t(organizationTypeLabelKeys[type])}:</span>
                    <span className="text-critical">
                        {t('salesforceDealImporter.dealLineItem.warnings.notImportedPartnerOrganization')}
                    </span>
                </>
            )}
        </span>
    );
}

type DealLineItemRowProps = {
    isSelected: boolean;
    hasSelectedRemark: boolean;
    onChange: (checkedState: boolean) => void;
    onChangeRemarkSelection: (checkedState: boolean) => void;
    onChangeManualSelectedProductType: (value: PRODUCT_TYPES | null) => void;
    dealLineItem: DealLineItem;
    manualSelectedProductType: PRODUCT_TYPES | null;
    organizations: NonNullable<GetDealResponse>['organizations'];
    compatibleDealLineItemIds: string[];
};
export function DealLineItemRow({
    dealLineItem,
    organizations,
    compatibleDealLineItemIds,
    isSelected,
    hasSelectedRemark,
    manualSelectedProductType,
    onChange,
    onChangeRemarkSelection,
    onChangeManualSelectedProductType,
}: DealLineItemRowProps) {
    const { t } = useTranslation();

    const { carrierOrganization, supplierOrganization } = useMemo(() => {
        let carrierOrganization = organizations.find(
            org => org.salesforceAccountId === dealLineItem.salesforceMetaData.carrierAccountId
        )?.organization;
        let supplierOrganization = organizations.find(
            org => org.salesforceAccountId === dealLineItem.salesforceMetaData.supplierAccountId
        )?.organization;

        // In a merchant case only one of the organizations is set in salesforce
        // but this one organization represents both the carrier and supplier
        // Therefore in a merchant case we have to set the same organization for carrier and supplier
        if (!carrierOrganization || !supplierOrganization) {
            const pricingStrategy = matchPricingStrategyByDealPricesAndProductType(
                dealLineItem,
                dealLineItem.product.productType || manualSelectedProductType
            );
            if (pricingStrategy === PRICING_STRATEGIES.MERCHANT) {
                const merchantOrganization = supplierOrganization || carrierOrganization;
                if (merchantOrganization) {
                    carrierOrganization = merchantOrganization;
                    supplierOrganization = merchantOrganization;
                }
            }
        }

        return {
            carrierOrganization,
            supplierOrganization,
        };
    }, [dealLineItem, organizations, manualSelectedProductType]);

    const isSelectable = useMemo(() => {
        return !!(
            compatibleDealLineItemIds.includes(dealLineItem.salesforceMetaData.opportunityLineItemId) &&
            (dealLineItem.salesforceMetaData.carrierAccountId === null || carrierOrganization) &&
            (dealLineItem.salesforceMetaData.supplierAccountId === null || supplierOrganization)
        );
    }, [dealLineItem.salesforceMetaData, carrierOrganization, supplierOrganization, compatibleDealLineItemIds]);

    const needsManualMapping = dealLineItem.product.productTypeConfig?.mappingStrategy === 'MANUAL';

    const isDisabled = !(isSelectable || (needsManualMapping && !manualSelectedProductType));
    return (
        <Table.Row
            key={dealLineItem.salesforceMetaData.opportunityLineItemId}
            className={clsx('group [&.group\\/row:hover_td]:bg-transparent', isDisabled && 'is-disabled')}
        >
            <Table.Cell>
                <div className={clsx(needsManualMapping && '-mt-px pt-4')}>
                    <Checkbox
                        disabled={!isSelectable}
                        checked={isSelected}
                        name="checkbox"
                        onCheckedChange={onChange}
                    />
                </div>
            </Table.Cell>
            <Table.Cell>
                {dealLineItem.product.productType && !needsManualMapping ? (
                    <Table.Item.Text className="group-[.is-disabled]:text-disabled">
                        {t(PRODUCT_NAME_LABEL[dealLineItem.product.productType])}
                    </Table.Item.Text>
                ) : (
                    <div className="max-w-[275px]">
                        <ProductTypeSelection
                            dealLineItemId={dealLineItem.salesforceMetaData.opportunityLineItemId}
                            options={dealLineItem.product.productTypeConfig.options ?? []}
                            value={manualSelectedProductType}
                            disabled={isSelected}
                            onChange={onChangeManualSelectedProductType}
                        />
                    </div>
                )}
            </Table.Cell>
            <Table.Cell>
                <div className="font-copy-md flex flex-col gap-4">
                    <div className="group-[.is-disabled]:text-disabled flex flex-col gap-1">
                        <span className="block">{dealLineItem.product.name}</span>
                        {dealLineItem.remark && (
                            <span className="text-subdued group-[.is-disabled]:text-disabled flex items-center gap-2">
                                {isSelected && (
                                    <Checkbox
                                        checked={hasSelectedRemark}
                                        name="checkbox"
                                        onCheckedChange={onChangeRemarkSelection}
                                    />
                                )}
                                {dealLineItem.remark}
                            </span>
                        )}
                    </div>
                    <div className="flex flex-col gap-1">
                        {dealLineItem.salesforceMetaData.supplierAccountId && (
                            <PartnerOrganization type="SUPPLIER" organization={supplierOrganization} />
                        )}
                        {dealLineItem.salesforceMetaData.carrierAccountId && (
                            <PartnerOrganization type="CARRIER" organization={carrierOrganization} />
                        )}
                    </div>
                </div>
            </Table.Cell>
            <Table.Cell>
                <Table.Item.Text className="group-[.is-disabled]:text-disabled">
                    {dealLineItem.quota} {t(SUPPORTED_UNIT_LABEL[dealLineItem.unit])}
                </Table.Item.Text>
            </Table.Cell>
            <Table.Cell>
                <PriceCell value={dealLineItem.materialPrice} currencyCode={dealLineItem.currencyCode} />
            </Table.Cell>
            <Table.Cell>
                <PriceCell value={dealLineItem.transportPrice} currencyCode={dealLineItem.currencyCode} />
            </Table.Cell>
            <Table.Cell>
                <PriceCell value={dealLineItem.francoPrice} currencyCode={dealLineItem.currencyCode} />
            </Table.Cell>
            <Table.Cell>
                <PriceCell value={dealLineItem.salesPrice} currencyCode={dealLineItem.currencyCode} />
            </Table.Cell>
            <Table.Cell>
                <Table.Item.Text>
                    <DealLineItemPositionReference
                        salesforceOpportunityId={dealLineItem.salesforceMetaData.opportunityId}
                        salesforceOpportunityLineItemId={dealLineItem.salesforceMetaData.opportunityLineItemId}
                    />
                </Table.Item.Text>
            </Table.Cell>
        </Table.Row>
    );
}
